/**
 * Stable per-browser device id for the operator consoles (Codex round 2 P2-3).
 *
 * The device fingerprint is sent at login AND on every refresh, where the server resolves it against the session's
 * device — so every tab and every call must present the SAME value. The previous `getItem → randomUUID → setItem`
 * could hand two first-use tabs different ids (only one persisted) and, when storage threw, a new id on every call.
 * This provider gets-or-creates the id under the cross-context lock (Web Locks) with a re-read, and memoizes it for
 * the page lifetime — including a random fallback when storage is unavailable.
 */
import { uuidv7 } from '@vello/core';
import { createCrossContextLock } from './session-refresh.js';
import { resolveLocalStorage } from './web-secure-store.js';
const defaultRandomId = () => globalThis.crypto?.randomUUID?.() ?? uuidv7();
export function createDeviceIdProvider(options) {
    const randomId = options.randomId ?? defaultRandomId;
    let memo;
    return () => {
        memo ??= (async () => {
            const storage = options.storage === undefined ? resolveLocalStorage() : (options.storage ?? undefined);
            if (storage === undefined)
                return randomId();
            const lock = options.lock ?? createCrossContextLock();
            try {
                return await lock.runExclusive(`vello.device-id:${options.storageKey}`, async () => {
                    const existing = storage.getItem(options.storageKey);
                    if (existing !== null && existing !== '')
                        return existing;
                    const id = randomId();
                    storage.setItem(options.storageKey, id);
                    const persisted = storage.getItem(options.storageKey);
                    return persisted !== null && persisted !== '' ? persisted : id;
                });
            }
            catch {
                return randomId(); // storage unavailable: one id for this page lifetime (memoized)
            }
        })();
        return memo;
    };
}
//# sourceMappingURL=device-id.js.map